import type { TaskType } from '../index'

// request 生成 task 后的主处理逻辑
// 不区分 ssr/export/client/global/page，必须由此方法处理（启动后--异步任务结束前）
export async function handleTasks(
  taskMap: { [propKey: string]: TaskType },
  onInitTaskCompleteSync: () => void,
  onAllComplete: () => void,
  onChange: (
    propKey: string,
    // 已确保 INITIAL->LOADING->CANCEL|COMPLETE，不会由于fallback等状态回转
    // 总是严格的触发整体的状态转换链条，即使实际 handle 时，task 自身可能已 loading 或 complete
    // 可能会在一个同步时序上流转多次状态
    status: 'INITIAL' | 'LOADING' | 'CANCEL' | 'COMPLETE',
    data?: any
  ) => void, // 注意，data 为最终结果对象，不再需要转换
  getGlobalAndPagePropKeys: () => string[],
  hasUserFallback: boolean,
  handleFallback: (task: TaskType) => Promise<void>,
  hasUserCallback: boolean,
  handleCallback: (
    task: TaskType
  ) => undefined | { [key: string]: any | Promise<any> },
  convertRawResponse: (task: TaskType) => any
): Promise<void> {
  // 初始 task 的任务表
  // 初始任务的等待列表 global(global.fallback) + page(page.fallback)，执行后的终态是  CANCEL 或 COMPLETE
  // 任务状态最终变为 CANCEL 或 COMPLETE 时回调
  const taskPromiseList: Array<[
    TaskType,
    Promise<void>
  ]> = ensureTaskToEndStatus(
    taskMap,
    hasUserFallback,
    handleFallback,
    convertRawResponse,
    (propKey, status, data) => {
      // global 和 page 的任务状态变化回调
      // data 为 convertRawResponse 转换后的值，可直接使用
      onChange(propKey, status, data)
    }
  )

  // 用于通知外部所有 task 已创建完毕
  // 注意，不含 callback 产生的新任务
  onInitTaskCompleteSync()

  if (!hasUserCallback) {
    // 不存在 userCallback 时，全部一起异步等待完成
    await Promise.all(taskPromiseList.map((x) => x[1]))
  } else {
    // 存在 userCallback 时行为特殊

    /*
      // 历史设计为
      // - 仅 page 支持 callback，仅 page 任务完成时尝试 callback
      // - 注意，每个任务完成都会回调通知，callback 将执行多次

      // 设计存在 BUG
      // 例如
      //  - 业务设置 if (result.globalA.status === 'COMPLETE' && result.localB.status === 'COMPLETE' ) { ... }
      //  - 当 localB 完成触发回调时，若 globalA 未完成，业务条件不满足，且不会再次以 localB 作为 propKey 回调
      // 另一层面，原设计假定了 page propKey + status 组合仅回调一次，业务代码可准确执行不会有歧义
      // 仍然是上面的例子
      //  - 业务设置 if (result.globalA.status === 'COMPLETE' && result.localB.status === 'COMPLETE' ) { return { newKey: ajax() } }
      //  - 当存在多个 propKey 时，if 规则将多次满足，发出多个 ajax 请求

      // 调整设计
      // - global 任务全部完成后，逐个按 propKey + status 进行 callback 回调
      // - 若业务有上文多次触发 ajax 的问题，需在 if 增加 propKey 的限制条件，确保仅执行一次
     */

    // 等待 global 的初始任务完成
    // 注意，此逻辑与 hydrate 的锁定无关。此除锁定不区分环境，是为了在 callback 执行前所有 global 就绪
    await Promise.all(
      taskPromiseList.filter((x) => x[0].category === 'global').map((x) => x[1])
    )

    const pageTaskPromiseList: Array<[
      TaskType,
      Promise<void>
    ]> = taskPromiseList.filter((x) => x[0].category === 'page')
    const {
      wrappedPageTaskPromiseList,
      extraCallbackPromiseList,
    } = ensurePageCallbackToEndStatus(
      pageTaskPromiseList,
      getGlobalAndPagePropKeys,
      handleCallback,
      (newPropKey, status, data) => {
        // 仅callback 产生的新异步任务状态变更时回调通知，page 自身的在上方
        // 此处的 data，不需要经由 convertRawResponse 转换
        onChange(newPropKey, status, data)
      }
    )
    // 等待 page 任务
    await Promise.all(wrappedPageTaskPromiseList)
    // 等待 page callback 过程中，产生的新数据
    await Promise.all(extraCallbackPromiseList)
  }

  // 注意，极端情况下
  // 若无任何 task 需要 await，onAllComplete 将在同步时序上回调
  onAllComplete()
}

// task 由 request 启动后
// 在此流转到终止状态（含fallback处理，fallback本身是task原任务的补充）
function ensureTaskToEndStatus(
  taskMap: { [propKey: string]: TaskType },
  hasUserFallback: boolean,
  handleFallback: (task: TaskType) => Promise<void>,
  convertRawResponse: (task: TaskType) => any,
  onStatusChange: (
    propKey: string,
    status: 'INITIAL' | 'LOADING' | 'CANCEL' | 'COMPLETE',
    data?: any
  ) => void
) {
  return Object.entries(taskMap).map(([propKey, task]) => {
    // 外部仅关注 INITIAL->LOADING->CANCEL|COMPLETE
    // 不关注内部 fallback 等引起的二次 LOADING
    async function handleTask() {
      // 先行触发
      onStatusChange(propKey, 'INITIAL')
      onStatusChange(propKey, 'LOADING')

      // 等待 LOADING 和 FALLBACK 完成
      switch (task.status) {
        case 'INITIAL':
          throw new Error('BUG 状态异常')
        case 'LOADING':
          await new Promise((resolve, reject) => {
            // 等待 LOADING 完成
            ;(task.request as any).then(resolve, reject)
          })
          break
        case 'CANCEL':
          if (task.category === 'global' || !hasUserFallback) {
            // global 不支持 fallback，直接视为终态
            // page 但未提供 fallback
          } else {
            // 执行 fallback
            await handleFallback(task)
            if (task.status === 'CANCEL') {
              // FALLBACK 后仍失败，无法启动，不再尝试
            } else if (task.status === 'LOADING') {
              // FALLBACK 后成功启动
              await new Promise((resolve, reject) => {
                // 等待 LOADING 完成
                ;(task.request as any).then(resolve, reject)
              })
            } else {
              throw new Error('BUG 状态异常')
            }
          }
          break
        default:
          // no stuff
          break
      }

      // 此时已终态
      switch (task.status) {
        case 'CANCEL':
          onStatusChange(propKey, 'CANCEL')
          break
        case 'COMPLETE':
          // 这里获取到的是原始 ajax/fetch response，需要进行一次转换
          onStatusChange(propKey, 'COMPLETE', convertRawResponse(task))
          break
        default:
          throw new Error(
            `[prefetchApi] 任务完成时状态异常 ${task.propKey} status:${task.status}`
          )
      }
    }

    return [task, handleTask()] as [TaskType, Promise<void>]
  })
}

// 前置条件，global 系列任务已完成，page 系列任务进行中
// 在 page 任务完成后，触发一次 callback 回调，并处理响应值
function ensurePageCallbackToEndStatus(
  taskPromiseList: Array<[TaskType, Promise<void>]>,
  getGlobalAndPagePropKeys: () => string[],
  handleCallback: (
    task: TaskType
  ) => undefined | { [key: string]: any | Promise<any> },
  onStatusChange: (
    propKey: string,
    status: 'INITIAL' | 'LOADING' | 'COMPLETE',
    data?: any
  ) => void
) {
  const extraCallbackPromiseList: Array<Promise<void>> = []
  const wrappedPageTaskPromiseList = taskPromiseList.map(
    ([task, taskPromise]) => {
      return taskPromise.then(() => {
        // page 任务完成后，触发 userCallback
        // 显然，每个 propKey 只会触发一次
        const callbackResult = handleCallback(task)
        if (!callbackResult) {
          return
        }

        const allPropKeys = getGlobalAndPagePropKeys()
        Object.entries(callbackResult).forEach(([key, objOrPromise]) => {
          if (Object.keys(allPropKeys).includes(key)) {
            throw new Error(
              'callback 不能返回已有任务（global+当前page）的 propKey'
            )
          }

          onStatusChange(key, 'INITIAL')
          if (objOrPromise && typeof objOrPromise.then === 'function') {
            // 返回的 value 是 promise
            onStatusChange(key, 'LOADING')
            // 增加一个 promise 完成的等待任务
            // 这里 .then 返回的 obj 已经是合法对象，不是原始的 ajax/fetch 对象，不再需要转换
            extraCallbackPromiseList.push(
              objOrPromise.then((obj: any) => {
                onStatusChange(key, 'COMPLETE', obj)
              })
            )
          } else {
            // 返回的 value 是普通对象
            onStatusChange(key, 'COMPLETE', objOrPromise)
          }
        })
      })
    }
  )
  return {
    wrappedPageTaskPromiseList,
    extraCallbackPromiseList,
  }
}
